/**
 * W-08 "Lot şeceresi" — the pure side of the genealogy screen: reading
 * `fn_lot_trace`'s jsonb into something typed, laying its nodes out in tiers, and
 * deciding what the mass-balance line says.
 *
 * The RPC is the authority on the chain and on what a role may see: cost fields
 * are simply absent for a role without `seesCosts`, so nothing here has to mask
 * anything. In Phase 2 the answer is almost always a single node — nothing has
 * been produced or shipped yet — which is why `expectedTierKeys` exists: the
 * screen draws the tiers the chain *will* have, greyed, instead of one lonely box
 * (§4.3).
 */
import { D } from '../money';

export const TRACE_DIRECTIONS = ['both', 'forward', 'backward'] as const;
export type TraceDirection = (typeof TRACE_DIRECTIONS)[number];

export function parseTraceDirection(raw: unknown): TraceDirection | undefined {
  return (TRACE_DIRECTIONS as readonly string[]).includes(raw as string) ? (raw as TraceDirection) : undefined;
}

export interface TraceNode {
  key: string;
  /** `lot`, or the movement reason a group node stands for (`sale`, `scrap`, …). */
  kind: string;
  tier: number;
  refId: string | null;
  code: string;
  title: string;
  sku: string | null;
  /** Base-unit decimal strings, exactly as the RPC returns them. */
  qty: string;
  onHand: string | null;
  unit: string | null;
  meta: { docNo: string | null; date: string | null; supplier: string | null };
  /** Only `origin` today — the lot the page is about. */
  badge: string | null;
  /** `lot_qc_status` for lot nodes. */
  status: string | null;
  /** Per base unit; absent (null) for a role that does not see costs. */
  unitCost: string | null;
}

export interface TraceEdge {
  from: string;
  to: string;
  qty: string;
  unit: string | null;
}

export interface TraceTier {
  index: number;
  kind: string;
  series: string | null;
  labelKey: string;
}

export interface TraceMassBalance {
  inputKg: string;
  outputKg: string;
  wasteKg: string;
  gapKg: string;
  complete: boolean;
  inProgress: boolean;
}

export interface LotTrace {
  origin: string | null;
  direction: TraceDirection;
  tiers: TraceTier[];
  nodes: TraceNode[];
  edges: TraceEdge[];
  massBalance: TraceMassBalance;
  /** Nodes the server left out (`p_max_depth`, 60 per tier). */
  truncated: number;
}

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : typeof v === 'number' ? String(v) : null);
const num = (v: unknown): string => (typeof v === 'number' || typeof v === 'string' ? String(v) : '0');

/**
 * Tolerant on purpose: `fn_lot_trace` is `jsonb`, so the client has no compile-time
 * shape, and a key a later phase adds (a tier for deliveries, a waste node) must
 * not throw the screen away. A missing `nodes` array reads as an empty chain,
 * which the page draws as "not linked to anything yet".
 */
export function parseLotTrace(raw: unknown): LotTrace | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = obj(raw);
  const mb = obj(root.massBalance);
  return {
    origin: str(root.origin),
    direction: parseTraceDirection(root.direction) ?? 'both',
    tiers: arr(root.tiers).map((t) => {
      const tier = obj(t);
      return {
        index: Number(tier.index ?? 0),
        kind: str(tier.kind) ?? 'lot',
        series: str(tier.series),
        labelKey: str(tier.labelKey) ?? 'stock.trace.tier.lot',
      };
    }),
    nodes: arr(root.nodes).map((n) => {
      const node = obj(n);
      const meta = obj(node.meta);
      return {
        key: str(node.key) ?? '',
        kind: str(node.kind) ?? 'lot',
        tier: Number(node.tier ?? 0),
        refId: str(node.refId),
        code: str(node.code) ?? '',
        title: str(node.title) ?? '',
        sku: str(node.sku),
        qty: num(node.qty),
        onHand: node.onHand === null || node.onHand === undefined ? null : num(node.onHand),
        unit: str(node.unit),
        meta: { docNo: str(meta.docNo), date: str(meta.date), supplier: str(meta.supplier) },
        badge: str(node.badge),
        status: str(node.status),
        unitCost: node.unitCost === null || node.unitCost === undefined ? null : num(node.unitCost),
      };
    }),
    edges: arr(root.edges).map((e) => {
      const edge = obj(e);
      return { from: str(edge.from) ?? '', to: str(edge.to) ?? '', qty: num(edge.qty), unit: str(edge.unit) };
    }),
    massBalance: {
      inputKg: num(mb.inputKg),
      outputKg: num(mb.outputKg),
      wasteKg: num(mb.wasteKg),
      gapKg: num(mb.gapKg),
      complete: mb.complete === true,
      inProgress: mb.inProgress === true,
    },
    truncated: Number(root.truncated ?? 0) || 0,
  };
}

/** Node kind → the tier label it would give a column (§4.4). */
const KIND_LABEL: Record<string, string> = {
  lot: 'stock.trace.tier.lot',
  production_order: 'stock.trace.tier.productionOrder',
  production_consume: 'stock.trace.tier.productionOrder',
  fba_inbound: 'stock.trace.tier.fbaShipment',
  sale: 'stock.trace.tier.sale',
  scrap: 'stock.trace.tier.waste',
  sample: 'stock.trace.tier.sample',
};

/** `lots.series` → the label the artboard gives that tier. */
export function seriesLabelKey(series: string | null | undefined): string {
  if (series === 'lot_bobbin') return 'stock.trace.tier.bobbin';
  if (series === 'lot_napkin') return 'stock.trace.tier.napkin';
  return 'stock.trace.tier.lot';
}

export function nodeLabelKey(kind: string, series?: string | null): string {
  if (kind === 'lot') return seriesLabelKey(series);
  return KIND_LABEL[kind] ?? 'stock.trace.tier.lot';
}

export interface TraceColumn {
  index: number;
  labelKey: string;
  series: string | null;
  nodes: TraceNode[];
  /** The "→" between this column and the next one. */
  arrow: boolean;
}

/**
 * Nodes grouped into the columns the graph draws, lowest tier first. A column's
 * label is the label of the kind that has the most nodes in it, ties going to the
 * first kind that is not waste — in the artboard column 3 holds a napkin lot and
 * a fire node and is labelled "Peçete lotu" (§4.4). An empty tier keeps the label
 * the RPC gave it, so the header row never collapses.
 */
export function traceColumns(trace: LotTrace): TraceColumn[] {
  const indices = new Set<number>();
  for (const tier of trace.tiers) indices.add(tier.index);
  for (const node of trace.nodes) indices.add(node.tier);
  const ordered = [...indices].sort((a, b) => a - b);

  const columns = ordered.map((index) => {
    const tier = trace.tiers.find((t) => t.index === index) ?? null;
    const nodes = trace.nodes.filter((n) => n.tier === index);
    return {
      index,
      series: tier?.series ?? null,
      nodes,
      labelKey: columnLabelKey(nodes, tier),
      arrow: false,
    };
  });

  // an arrow leaves a column that holds something and points at the next column
  // that does — an empty placeholder tier neither starts nor receives one
  for (let i = 0; i < columns.length; i++) {
    columns[i]!.arrow = columns[i]!.nodes.length > 0 && columns.slice(i + 1).some((c) => c.nodes.length > 0);
  }
  return columns;
}

function columnLabelKey(nodes: TraceNode[], tier: TraceTier | null): string {
  if (nodes.length === 0) return tier?.series ? seriesLabelKey(tier.series) : (tier?.labelKey ?? 'stock.trace.tier.lot');
  const counts = new Map<string, number>();
  for (const node of nodes) counts.set(node.kind, (counts.get(node.kind) ?? 0) + 1);
  const entries = [...counts.entries()];
  const most = Math.max(...entries.map(([, count]) => count));
  const tied = entries.filter(([, count]) => count === most);
  const chosen = tied.find(([kind]) => kind !== 'scrap') ?? tied[0];
  return nodeLabelKey(chosen?.[0] ?? 'lot', tier?.series);
}

/** Columns that hold at least one node — what "{{count}} kademe" counts. */
export function traceTierCount(trace: LotTrace): number {
  return new Set(trace.nodes.map((n) => n.tier)).size;
}

/**
 * The tiers a chain of this series will grow, for the greyed placeholder headers
 * of §4.3. Phase 2 can only ever answer with tier 1, and a bobbin lot's future is
 * known: production order → napkin lot → FBA shipment.
 */
export function expectedTierKeys(series: string | null | undefined): string[] {
  if (series === 'lot_bobbin') return ['stock.trace.tier.productionOrder', 'stock.trace.tier.napkin', 'stock.trace.tier.fbaShipment'];
  if (series === 'lot_napkin') return ['stock.trace.tier.fbaShipment', 'stock.trace.tier.sale'];
  return ['stock.trace.tier.sale'];
}

export type MassBalanceState = 'hidden' | 'balanced' | 'gap' | 'inProgress';

/**
 * §4.6. Nothing consumed means nothing to balance, and the line is hidden rather
 * than printed as "0 kg giriş". An unexplained gap is more than half a percent of
 * the input — below that it is rounding in the kg conversions.
 */
export function massBalanceState(mb: TraceMassBalance, tolerancePct = 0.5): MassBalanceState {
  const input = D(mb.inputKg);
  if (input.isZero() && D(mb.outputKg).isZero() && D(mb.wasteKg).isZero()) return 'hidden';
  if (mb.inProgress) return 'inProgress';
  const gap = input.minus(mb.outputKg).minus(mb.wasteKg).abs();
  return gap.greaterThan(input.abs().times(tolerancePct).dividedBy(100)) ? 'gap' : 'balanced';
}

/** The gap the warning appends, always positive. */
export function massBalanceGap(mb: TraceMassBalance): string {
  return D(mb.inputKg).minus(mb.outputKg).minus(mb.wasteKg).abs().toString();
}

// ── side panel (`get_trace_node_detail`) ────────────────────────────────────
export interface TraceCostRow {
  key: string;
  /** Already formatted by the server when it is a plain value; otherwise built from the parts. */
  amount: string | null;
  currency: string | null;
  per: string | null;
  /** Drives the row's weight and colour (§4.8). */
  style: 'base' | 'addon' | 'subtotal' | 'total' | 'fx';
  /** `fx` rows carry a rate and a pair instead of an amount. */
  rate: string | null;
  pair: string | null;
  date: string | null;
}

const STYLES = ['base', 'addon', 'subtotal', 'total', 'fx'] as const;

/**
 * `cost_breakdown` is written by server functions only, and Phase 2 writes none
 * (the opening import fills `lot_costs.unit_cost_base` and nothing else), so this
 * normally returns an empty list and the panel says the cost has not been broken
 * down yet. The shape is read defensively because Phase 3 (landed cost) and
 * Phase 5 (production close) each add rows of their own.
 */
export function parseCostRows(raw: unknown): TraceCostRow[] {
  return arr(raw)
    .map((entry) => {
      const row = obj(entry);
      const value = row.value === undefined ? row : obj(row.value);
      const plain = row.value !== undefined && (typeof row.value === 'number' || typeof row.value === 'string') ? String(row.value) : null;
      const style = STYLES.includes(str(value.style) as (typeof STYLES)[number]) ? (str(value.style) as TraceCostRow['style']) : 'base';
      return {
        key: str(row.key) ?? '',
        amount: plain ?? str(value.amount),
        currency: str(value.currency),
        per: str(value.per),
        style,
        rate: str(value.rate),
        pair: str(value.pair),
        date: str(value.date),
      };
    })
    .filter((row) => row.key !== '');
}

export interface TraceNodeLocation {
  code: string;
  qtyBase: string;
}

export interface TraceLotDetail {
  kind: 'lot';
  id: string | null;
  code: string;
  item: { id: string | null; sku: string | null; nameTr: string | null; attributes: Record<string, unknown> };
  qty: string;
  unit: string | null;
  onHand: string;
  locations: TraceNodeLocation[];
  receivedAt: string | null;
  expiryDate: string | null;
  supplierName: string | null;
  supplierLotNo: string | null;
  qcStatus: string | null;
  isBlocked: boolean;
  attributes: Record<string, unknown>;
  /** Per base unit; null when the role does not see costs *or* no cost exists. */
  unitCostBase: string | null;
  costRows: TraceCostRow[];
}

export interface TraceMovementDetail {
  /** Literal, so the union narrows on it; the RPC's own kind is in `reason`. */
  kind: 'movements';
  /** The movement reason the group stands for (`sale`, `scrap`, `fba_inbound`, …). */
  reason: string;
  id: string | null;
  movements: {
    id: string | null;
    no: string | null;
    occurredAt: string | null;
    qtyBase: string;
    unit: string | null;
    reason: string | null;
    fromLocation: string | null;
    toLocation: string | null;
    valueBase: string | null;
  }[];
}

export type TraceNodeDetail = TraceLotDetail | TraceMovementDetail;

export function parseTraceNodeDetail(raw: unknown): TraceNodeDetail | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = obj(raw);
  if (root.kind === 'lot') {
    const item = obj(root.item);
    return {
      kind: 'lot',
      id: str(root.id),
      code: str(root.code) ?? '',
      item: { id: str(item.id), sku: str(item.sku), nameTr: str(item.name_tr), attributes: obj(item.attributes) },
      qty: num(root.qty),
      unit: str(root.unit),
      onHand: num(root.on_hand),
      locations: arr(root.locations).map((l) => {
        const loc = obj(l);
        return { code: str(loc.location_code) ?? '', qtyBase: num(loc.qty_base) };
      }),
      receivedAt: str(root.received_at),
      expiryDate: str(root.expiry_date),
      supplierName: str(root.supplier_name),
      supplierLotNo: str(root.supplier_lot_no),
      qcStatus: str(root.qc_status),
      isBlocked: root.is_blocked === true,
      attributes: obj(root.attributes),
      unitCostBase: root.unit_cost_base === null || root.unit_cost_base === undefined ? null : num(root.unit_cost_base),
      costRows: parseCostRows(root.cost_rows),
    };
  }
  return {
    kind: 'movements',
    reason: str(root.kind) ?? 'movement',
    id: str(root.id),
    movements: arr(root.movements).map((m) => {
      const mv = obj(m);
      return {
        id: str(mv.movement_id),
        no: str(mv.no),
        occurredAt: str(mv.occurred_at),
        qtyBase: num(mv.qty_base),
        unit: str(mv.unit),
        reason: str(mv.reason),
        fromLocation: str(mv.from_location),
        toLocation: str(mv.to_location),
        valueBase: mv.value_base === null || mv.value_base === undefined ? null : num(mv.value_base),
      };
    }),
  };
}

/**
 * The status pill of the panel header (§4.7): the lot's own state wins over its
 * quality status, because "Karantina" is what stops it being issued. Returns the
 * i18n suffix under `stock.trace.status.*` and the chip tone.
 */
export function traceStatusKey(input: {
  kind: string;
  qcStatus?: string | null;
  isBlocked?: boolean | null;
  onHand?: string | null;
}): { key: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral' } {
  if (input.kind !== 'lot') return { key: 'inChain', tone: 'neutral' };
  if (input.isBlocked) return { key: 'quarantine', tone: 'warning' };
  switch (input.qcStatus) {
    case 'accepted':
      return { key: 'accepted', tone: 'success' };
    case 'passed':
      return { key: 'passed', tone: 'success' };
    case 'pending':
      return { key: 'pending', tone: 'warning' };
    case 'failed':
      return { key: 'failed', tone: 'danger' };
    case 'quarantine':
      return { key: 'quarantine', tone: 'warning' };
    default:
      break;
  }
  const onHand = D(input.onHand ?? 0);
  if (onHand.isNegative()) return { key: 'negative', tone: 'danger' };
  return onHand.isZero() ? { key: 'depleted', tone: 'neutral' } : { key: 'inStock', tone: 'neutral' };
}
